import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search, Plus, Trash2, RefreshCw,
  LayoutGrid, List, CheckCircle2, Clock, XCircle,
  ShieldOff, UserX, Camera, CreditCard as CardIcon, Hash, Users,
  ChevronLeft, ChevronRight,
} from 'lucide-react'
import { getUsers, deleteUser, type User } from '@/api/users'
import { listMemberships, type Membership } from '@/api/memberships'
import { getPlanUsage } from '@/api/config'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'

type Filter = 'all' | 'active' | 'expiring' | 'expired' | 'no-membership' | 'no-access' | 'no-face'
type ViewMode = 'cards' | 'table'

interface MembershipWithPlan extends Membership { plan_name?: string }

function formatDate(d: string) {
  return new Date(d.slice(0, 10) + 'T12:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })
}

function daysUntil(dateStr: string) {
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const d = new Date(dateStr.slice(0, 10) + 'T12:00:00')
  return Math.round((d.getTime() - today.getTime()) / 86_400_000)
}

function getMembershipState(m: MembershipWithPlan | undefined) {
  if (!m) return 'none' as const
  if (m.status !== 'active') return 'expired' as const
  const days = daysUntil(m.end_date)
  if (days < 0) return 'overdue' as const
  if (days <= 7) return 'expiring' as const
  return 'active' as const
}

type MemberState = ReturnType<typeof getMembershipState>

const PAGE_SIZE = 30

// ── MemberList ────────────────────────────────────────────────────────────────
export function MemberList() {
  const navigate = useNavigate()
  const [users, setUsers]           = useState<User[]>([])
  const [memberships, setMemberships] = useState<MembershipWithPlan[]>([])
  const [search, setSearch]         = useState('')
  const [filter, setFilter]         = useState<Filter>('all')
  const [loading, setLoading]       = useState(true)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [planInfo, setPlanInfo] = useState<{ current: number; limit: number; enforcement: boolean } | null>(null)
  const [viewMode, setViewMode]     = useState<ViewMode>(() =>
    (localStorage.getItem('members-view') as ViewMode) ?? 'cards'
  )
  const [page, setPage] = useState(1)

  function setView(v: ViewMode) {
    setViewMode(v)
    localStorage.setItem('members-view', v)
  }

  function load() {
    setLoading(true)
    Promise.allSettled([getUsers(), listMemberships(), getPlanUsage()])
      .then(([usersRes, membRes, planRes]) => {
        if (usersRes.status === 'fulfilled') setUsers(usersRes.value.users)
        else toast.error('Error al cargar miembros')
        if (membRes.status === 'fulfilled') setMemberships(membRes.value.memberships as MembershipWithPlan[])
        else toast.error('Error al cargar membresías')
        if (planRes.status === 'fulfilled') {
          const r = planRes.value.resources.find(x => x.key === 'members')
          if (r) setPlanInfo({ current: r.current, limit: r.limit, enforcement: planRes.value.enforcement })
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])
  useEffect(() => { setPage(1) }, [filter, search])

  const membershipMap = useMemo(() => {
    const map = new Map<string, MembershipWithPlan>()
    memberships.forEach(m => map.set(m.user_id, m))
    return map
  }, [memberships])

  const counts = useMemo(() => {
    let active = 0, expiring = 0, expired = 0, noMembership = 0, noAccess = 0, noFace = 0
    for (const u of users) {
      const state = getMembershipState(membershipMap.get(u.id))
      if (state === 'active') active++
      else if (state === 'expiring') expiring++
      else if (state === 'overdue' || state === 'expired') expired++
      else noMembership++
      if (!u.face_uploaded && !u.card_no && !u.pin) noAccess++
      if (!u.face_uploaded) noFace++
    }
    return { active, expiring, expired, noMembership, noAccess, noFace }
  }, [users, membershipMap])

  const filtered = useMemo(() => {
    const result = users
      .filter(u => {
        const q = search.toLowerCase()
        if (q && !u.name.toLowerCase().includes(q) && !u.employee_no.toLowerCase().includes(q)) return false
        const state = getMembershipState(membershipMap.get(u.id))
        if (filter === 'active')       return state === 'active'
        if (filter === 'expiring')     return state === 'expiring'
        if (filter === 'expired')      return state === 'overdue' || state === 'expired'
        if (filter === 'no-membership') return state === 'none'
        if (filter === 'no-access')    return !u.face_uploaded && !u.card_no && !u.pin
        if (filter === 'no-face')      return !u.face_uploaded
        return true
      })
      .sort((a, b) => {
        const order: Record<string, number> = { active: 0, expiring: 1, overdue: 2, expired: 2, none: 3 }
        const sa = getMembershipState(membershipMap.get(a.id))
        const sb = getMembershipState(membershipMap.get(b.id))
        const diff = (order[sa] ?? 9) - (order[sb] ?? 9)
        return diff !== 0 ? diff : a.name.localeCompare(b.name)
      })
    return result
  }, [users, membershipMap, search, filter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paginated  = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  )

  async function confirmDelete() {
    if (!deleteTarget) return
    try {
      await deleteUser(deleteTarget.id)
      setUsers(prev => prev.filter(u => u.id !== deleteTarget.id))
      toast.success('Miembro eliminado')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al eliminar')
    } finally { setDeleteTarget(null) }
  }

  const goTo = (id: string, tab?: string) =>
    navigate(tab ? `/members/${id}?tab=${tab}` : `/members/${id}`)

  const today = new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })
  const isAtLimit = Boolean(planInfo && planInfo.enforcement && planInfo.current >= planInfo.limit)

  return (
    <div className="flex flex-col gap-5">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-text-primary font-bold text-xl leading-tight">Miembros</h1>
          <p className="text-text-muted text-xs mt-1 capitalize">
            {today} · {users.length} registros
            {planInfo && (
              <span className={`ml-2 font-semibold ${isAtLimit ? 'text-red-500' : ''}`}>
                · {planInfo.current} / {planInfo.limit} del plan
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Toggle vista */}
          <div className="flex items-center rounded-xl border border-border overflow-hidden">
            <button
              onClick={() => setView('cards')}
              aria-label="Vista tarjetas"
              className={`px-3 py-2.5 transition-colors cursor-pointer ${viewMode === 'cards' ? 'bg-accent text-background' : 'text-text-muted hover:text-text-primary hover:bg-surface'}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setView('table')}
              aria-label="Vista tabla"
              className={`px-3 py-2.5 transition-colors cursor-pointer ${viewMode === 'table' ? 'bg-accent text-background' : 'text-text-muted hover:text-text-primary hover:bg-surface'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          <button onClick={load} aria-label="Actualizar" className="p-2.5 text-text-muted hover:text-text-primary transition-colors rounded-xl hover:bg-surface border border-border cursor-pointer">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => !isAtLimit && navigate('/members/new')}
            disabled={isAtLimit}
            title={isAtLimit ? `Límite del plan alcanzado (${planInfo?.current}/${planInfo?.limit}). Contacta a soporte para ampliar.` : undefined}
            className={`flex items-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl transition-colors shadow-lg ${
              isAtLimit
                ? 'bg-text-muted/20 text-text-muted cursor-not-allowed shadow-none'
                : 'bg-accent text-background hover:bg-accent-hover cursor-pointer shadow-accent/20'
            }`}
          >
            <Plus className="w-4 h-4" /> Nuevo miembro
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-surface border border-border rounded-2xl px-5 py-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0">
            <Users className="w-5 h-5 text-accent" />
          </div>
          <div>
            <p className="text-accent text-xl font-black leading-tight">{users.length}</p>
            <p className="text-text-muted text-xs mt-0.5">Total miembros</p>
          </div>
        </div>
        <div className="bg-surface border border-border rounded-2xl px-5 py-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          </div>
          <div>
            <p className="text-emerald-500 text-xl font-black leading-tight">{counts.active}</p>
            <p className="text-text-muted text-xs mt-0.5">Activos</p>
          </div>
        </div>
        <div className="bg-surface border border-border rounded-2xl px-5 py-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
            <Clock className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <p className="text-amber-600 dark:text-amber-400 text-xl font-black leading-tight">{counts.expiring}</p>
            <p className="text-text-muted text-xs mt-0.5">Por vencer</p>
          </div>
        </div>
      </div>

      {/* ── Búsqueda ── */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por nombre o cédula..."
          className="w-full bg-surface border border-border rounded-xl pl-9 pr-3 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </div>

      {/* ── Filtros ── */}
      <div className="flex gap-2 flex-wrap">
        <Chip active={filter === 'all'} onClick={() => setFilter('all')}>
          Todos <N n={users.length} />
        </Chip>
        {counts.active > 0 && (
          <Chip active={filter === 'active'} color="green" icon={<CheckCircle2 className="w-3 h-3" />} onClick={() => setFilter('active')}>
            Activos <N n={counts.active} />
          </Chip>
        )}
        {counts.expiring > 0 && (
          <Chip active={filter === 'expiring'} color="amber" icon={<Clock className="w-3 h-3" />} onClick={() => setFilter('expiring')}>
            Por vencer <N n={counts.expiring} />
          </Chip>
        )}
        {counts.expired > 0 && (
          <Chip active={filter === 'expired'} color="red" icon={<XCircle className="w-3 h-3" />} onClick={() => setFilter('expired')}>
            Vencidos <N n={counts.expired} />
          </Chip>
        )}
        {counts.noMembership > 0 && (
          <Chip active={filter === 'no-membership'} icon={<UserX className="w-3 h-3" />} onClick={() => setFilter('no-membership')}>
            Sin membresía <N n={counts.noMembership} />
          </Chip>
        )}
        {counts.noAccess > 0 && (
          <Chip active={filter === 'no-access'} color="amber" icon={<ShieldOff className="w-3 h-3" />} onClick={() => setFilter('no-access')}>
            Sin acceso <N n={counts.noAccess} />
          </Chip>
        )}
        {counts.noFace > 0 && (
          <Chip active={filter === 'no-face'} icon={<Camera className="w-3 h-3" />} onClick={() => setFilter('no-face')}>
            Sin foto <N n={counts.noFace} />
          </Chip>
        )}
      </div>

      {/* ── Contenido ── */}
      {loading ? (
        <div className="py-16 text-center">
          <RefreshCw className="w-5 h-5 text-text-muted animate-spin mx-auto mb-2" />
          <p className="text-text-muted text-sm">Cargando miembros...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-text-muted text-sm">{search ? 'Sin resultados para esa búsqueda' : 'No hay miembros en esta categoría'}</p>
        </div>
      ) : viewMode === 'cards' ? (
        /* ── Vista tarjetas ── */
        <div className="grid grid-cols-3 gap-4">
          {paginated.map(u => (
            <MemberCard
              key={u.id}
              user={u}
              membership={membershipMap.get(u.id)}
              state={getMembershipState(membershipMap.get(u.id))}
              onNavigate={goTo}
              onDelete={(id, name) => setDeleteTarget({ id, name })}
            />
          ))}
        </div>
      ) : (
        /* ── Vista tabla ── */
        <div className="bg-surface rounded-2xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-background/40">
                <th className="text-left px-4 py-3 text-text-muted text-[10px] font-bold uppercase tracking-widest">Miembro</th>
                <th className="text-left px-4 py-3 text-text-muted text-[10px] font-bold uppercase tracking-widest hidden sm:table-cell">Cédula</th>
                <th className="text-left px-4 py-3 text-text-muted text-[10px] font-bold uppercase tracking-widest">Membresía</th>
                <th className="text-left px-4 py-3 text-text-muted text-[10px] font-bold uppercase tracking-widest hidden md:table-cell">Acceso</th>
                <th className="text-right px-4 py-3 text-text-muted text-[10px] font-bold uppercase tracking-widest">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(u => {
                const membership = membershipMap.get(u.id)
                const state = getMembershipState(membership)
                const noAccess = !u.face_uploaded && !u.card_no && !u.pin
                const rowBg =
                  state === 'overdue' || state === 'expired' ? 'bg-red-950/8 border-l-2 border-l-red-500/50' :
                  state === 'expiring' ? 'bg-amber-950/8 border-l-2 border-l-amber-500/50' :
                  noAccess ? 'bg-amber-950/5' : ''
                return (
                  <tr key={u.id} className={`border-b border-border last:border-0 hover:bg-background/20 transition-colors ${rowBg}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <SmallAvatar name={u.name} hasFace={u.face_uploaded} />
                        <div>
                          <button onClick={() => goTo(u.id)} className="text-text-primary hover:text-accent font-semibold text-sm transition-colors cursor-pointer">
                            {u.name}
                          </button>
                          <p className="text-text-muted text-[10px]">Alta: {formatDate(u.created_at)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-text-muted text-xs hidden sm:table-cell font-mono">{u.employee_no}</td>
                    <td className="px-4 py-3"><StatusBadge membership={membership} state={state} /></td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {noAccess ? (
                        <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 px-2 py-0.5 rounded-full">
                          <ShieldOff className="w-2.5 h-2.5" /> Sin biométricos
                        </span>
                      ) : (
                        <div className="flex items-center gap-1">
                          <AccessPill active={u.face_uploaded} label="Cara" />
                          <AccessPill active={!!u.card_no} label="Tarjeta" />
                          <AccessPill active={!!u.pin} label="PIN" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {(state === 'overdue' || state === 'expired' || state === 'expiring') && (
                          <button onClick={() => goTo(u.id, 'membresia')} className="text-[10px] bg-accent text-background px-2 py-1 rounded-md font-bold hover:bg-accent-hover transition-colors cursor-pointer">Renovar</button>
                        )}
                        {state === 'none' && (
                          <button onClick={() => goTo(u.id, 'membresia')} className="text-[10px] bg-accent text-background px-2 py-1 rounded-md font-bold hover:bg-accent-hover transition-colors cursor-pointer">Asignar</button>
                        )}
                        {noAccess && (
                          <button onClick={() => goTo(u.id, 'biometricos')} className="text-[10px] border border-amber-400 dark:border-amber-700 text-amber-700 dark:text-amber-400 px-2 py-1 rounded-md font-semibold hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors cursor-pointer">Config.</button>
                        )}
                        <button onClick={() => goTo(u.id)} className="text-[10px] text-text-muted hover:text-accent transition-colors px-1.5 cursor-pointer">Ver →</button>
                        <button onClick={() => setDeleteTarget({ id: u.id, name: u.name })} aria-label="Eliminar" className="p-1 text-text-muted hover:text-red-600 dark:hover:text-red-400 rounded transition-colors cursor-pointer">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Paginación ── */}
      {filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-4 py-2">
          <p className="text-text-muted text-xs">
            Mostrando <span className="font-semibold text-text-primary">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)}</span> de <span className="font-semibold text-text-primary">{filtered.length}</span> miembros
          </p>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg border border-border text-text-muted hover:text-text-primary hover:bg-surface disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-text-muted px-2">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded-lg border border-border text-text-muted hover:text-text-primary hover:bg-surface disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Modal eliminar ── */}
      <Dialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <DialogContent className="bg-surface border-border text-text-primary max-w-sm">
          <DialogHeader><DialogTitle>Eliminar miembro</DialogTitle></DialogHeader>
          <p className="text-text-muted text-sm mt-2">
            ¿Eliminar a <strong className="text-text-primary">{deleteTarget?.name}</strong>? Esta acción no se puede deshacer.
          </p>
          <DialogFooter className="mt-4">
            <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors cursor-pointer">Cancelar</button>
            <button onClick={confirmDelete} className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors cursor-pointer">Eliminar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ── Tarjeta de miembro ────────────────────────────────────────────────────────
const CARD_STYLE = {
  active:   { stripe: 'border-l-emerald-500', av: 'bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-400' },
  expiring: { stripe: 'border-l-amber-500',   av: 'bg-amber-100   dark:bg-amber-950/80  text-amber-700   dark:text-amber-400'   },
  overdue:  { stripe: 'border-l-red-500',     av: 'bg-red-100     dark:bg-red-950/80    text-red-700     dark:text-red-400'     },
  expired:  { stripe: 'border-l-red-500',     av: 'bg-red-100     dark:bg-red-950/80    text-red-700     dark:text-red-400'     },
  none:     { stripe: 'border-l-border',      av: 'bg-background  text-text-muted' },
} as const

function MemberCard({ user, membership, state, onNavigate, onDelete }: {
  user: User
  membership: MembershipWithPlan | undefined
  state: MemberState
  onNavigate: (id: string, tab?: string) => void
  onDelete: (id: string, name: string) => void
}) {
  const noAccess = !user.face_uploaded && !user.card_no && !user.pin
  const s = CARD_STYLE[state]
  const ini = user.name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
  const needsAction = state === 'expiring' || state === 'overdue' || state === 'expired' || state === 'none'

  return (
    <div className={`bg-surface border border-border border-l-4 ${s.stripe} rounded-xl flex flex-col hover:shadow-lg hover:shadow-black/20 hover:-translate-y-0.5 transition-all duration-200 group`}>

      {/* ── Cabecera: avatar + nombre ── */}
      <div className="p-4 flex items-center gap-3">
        <div className={`w-12 h-12 rounded-full shrink-0 flex items-center justify-center font-black text-lg select-none ${s.av}`}>
          {ini}
        </div>
        <div className="flex-1 min-w-0">
          <button
            onClick={() => onNavigate(user.id)}
            className="text-text-primary font-bold text-sm hover:text-accent transition-colors cursor-pointer leading-snug text-left w-full truncate block"
          >
            {user.name}
          </button>
          <div className="flex items-center gap-1 mt-0.5">
            <Hash className="w-2.5 h-2.5 text-text-muted shrink-0" />
            <span className="text-text-muted text-[10px] font-mono">{user.employee_no}</span>
          </div>
        </div>
        <button
          onClick={e => { e.stopPropagation(); onDelete(user.id, user.name) }}
          aria-label="Eliminar"
          className="shrink-0 p-1.5 rounded-lg text-text-muted/40 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* ── Membresía + acceso ── */}
      <div className="px-4 pb-4 flex flex-col gap-2.5">
        <StatusBadge membership={membership} state={state} />
        <div className="flex items-center gap-1.5">
          <AccessPill active={user.face_uploaded} label="Cara"    icon={<Camera   className="w-2.5 h-2.5" />} />
          <AccessPill active={!!user.card_no}     label="Tarjeta" icon={<CardIcon className="w-2.5 h-2.5" />} />
          <AccessPill active={!!user.pin}          label="PIN"    icon={<Hash     className="w-2.5 h-2.5" />} />
        </div>
      </div>

      {/* ── Botones ── */}
      <div className="border-t border-border p-3 flex flex-col gap-2 mt-auto">
        {(state === 'expiring' || state === 'overdue' || state === 'expired') && (
          <button
            onClick={() => onNavigate(user.id, 'membresia')}
            className="w-full py-2.5 bg-accent text-background text-sm font-bold rounded-lg hover:bg-accent-hover transition-colors cursor-pointer"
          >
            Renovar membresía
          </button>
        )}
        {state === 'none' && (
          <button
            onClick={() => onNavigate(user.id, 'membresia')}
            className="w-full py-2.5 bg-accent text-background text-sm font-bold rounded-lg hover:bg-accent-hover transition-colors cursor-pointer"
          >
            Asignar membresía
          </button>
        )}
        {noAccess && state === 'active' && (
          <button
            onClick={() => onNavigate(user.id, 'biometricos')}
            className="w-full py-2.5 border border-amber-400 dark:border-amber-700/60 text-amber-700 dark:text-amber-400 text-sm font-semibold rounded-lg hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors cursor-pointer"
          >
            Configurar acceso
          </button>
        )}
        <button
          onClick={() => onNavigate(user.id)}
          className={`w-full py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
            needsAction || noAccess
              ? 'border border-border text-text-muted hover:border-accent/40 hover:text-accent'
              : 'bg-accent/10 text-accent hover:bg-accent/20'
          }`}
        >
          Ver perfil →
        </button>
      </div>
    </div>
  )
}

// ── Avatares ──────────────────────────────────────────────────────────────────
function SmallAvatar({ name, hasFace }: { name: string; hasFace: boolean }) {
  const ini = name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
  return (
    <div className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 ${hasFace ? 'bg-accent/20 border-accent/40' : 'bg-surface border-border'}`}>
      <span className={`text-[10px] font-bold ${hasFace ? 'text-accent' : 'text-text-muted'}`}>{ini}</span>
    </div>
  )
}

// ── Badge de membresía ────────────────────────────────────────────────────────
function StatusBadge({ membership, state }: { membership: MembershipWithPlan | undefined; state: MemberState }) {
  if (!membership || state === 'none') {
    return (
      <div className="flex items-center gap-1.5 text-text-muted text-xs">
        <span className="w-1.5 h-1.5 rounded-full bg-border" />
        Sin membresía
      </div>
    )
  }

  const days = daysUntil(membership.end_date)
  const cfg = {
    active:   { bg: 'bg-emerald-50 dark:bg-emerald-950/25 border-emerald-200 dark:border-emerald-900/40', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500' },
    expiring: { bg: 'bg-amber-50   dark:bg-amber-950/25   border-amber-200   dark:border-amber-900/40',   text: 'text-amber-700   dark:text-amber-400',   dot: 'bg-amber-500' },
    overdue:  { bg: 'bg-red-50     dark:bg-red-950/25     border-red-200     dark:border-red-900/40',     text: 'text-red-700     dark:text-red-400',     dot: 'bg-red-500' },
    expired:  { bg: 'bg-red-50     dark:bg-red-950/25     border-red-200     dark:border-red-900/40',     text: 'text-red-700     dark:text-red-400',     dot: 'bg-red-500' },
    none:     { bg: '', text: '', dot: '' },
  }[state]

  const label =
    state === 'active'   ? `Vence ${formatDate(membership.end_date)}` :
    state === 'expiring' ? (days === 0 ? 'Vence hoy' : days === 1 ? 'Vence mañana' : `${days} días`) :
    `Venció ${formatDate(membership.end_date)}`

  return (
    <div className="flex flex-col gap-0.5">
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] border font-semibold ${cfg.bg} ${cfg.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot} ${state === 'active' ? 'animate-pulse' : ''}`} />
        {label}
      </span>
      {membership.plan_name && (
        <span className="text-text-muted text-[10px] px-1">{membership.plan_name}</span>
      )}
    </div>
  )
}

// ── Chip de filtro ────────────────────────────────────────────────────────────
function Chip({ active, color, icon, onClick, children }: {
  active: boolean; color?: 'green' | 'amber' | 'red'
  icon?: React.ReactNode; onClick: () => void; children: React.ReactNode
}) {
  const cm = {
    green: { on: 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800', off: 'text-emerald-600 dark:text-emerald-600 border-emerald-200 dark:border-emerald-900/40 hover:bg-emerald-50 dark:hover:bg-emerald-950/20' },
    amber: { on: 'bg-amber-100   dark:bg-amber-950/40   text-amber-700   dark:text-amber-400   border-amber-300   dark:border-amber-800',   off: 'text-amber-600   dark:text-amber-600   border-amber-200   dark:border-amber-900/40   hover:bg-amber-50   dark:hover:bg-amber-950/20' },
    red:   { on: 'bg-red-100     dark:bg-red-950/40     text-red-700     dark:text-red-400     border-red-300     dark:border-red-800',     off: 'text-red-600     dark:text-red-600     border-red-200     dark:border-red-900/40     hover:bg-red-50     dark:hover:bg-red-950/20' },
  }
  const cls = color
    ? (active ? cm[color].on : cm[color].off)
    : (active ? 'bg-accent/10 text-accent border-accent/30' : 'text-text-muted border-border hover:text-text-primary hover:bg-surface')
  return (
    <button onClick={onClick} className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors border cursor-pointer ${cls}`}>
      {icon}{children}
    </button>
  )
}

function N({ n }: { n: number }) {
  return <span className="opacity-60">({n})</span>
}

// ── Pill de acceso ────────────────────────────────────────────────────────────
function AccessPill({ active, label, icon }: { active: boolean; label: string; icon?: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] border transition-colors ${
      active
        ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900/40'
        : 'text-text-muted border-border opacity-40'
    }`}>
      {icon}{label}
    </span>
  )
}
